import React, { useEffect, useRef } from "react";
import CardImage from "assets/images/card.png";
import useShowLayout from "hooks/useShowLayout";
import useLayoutUtils from "hooks/useLayoutUtils";
import { useParams } from "react-router-dom";
import useIsPc from "hooks/useIsPc";
import useIsMobile from "hooks/useIsMobile";
import useIsTablet from "hooks/useIsTablet";
import useCartigliLoader from "hooks/generated-page/useCartigliLoader";
import Lottie from "lottie-react";
import useLottieLoader from "hooks/generated-page/useLottieLoader";
import { allowedCharacters } from "configs/generated-page-configs";
import downloadIcon from "assets/icons/download.svg";
import shopIcon from "assets/icons/buy.svg";
import "./result.css";
import useHieroglyphDownloader from "hooks/generated-page/useHieroglyphDownloader";

import { useLayoutContext } from "../../layout/layout-provider";
import InfoPageGeneratedPage from "../infopage/InfoPageGeneratedPage";
import { useScribeContext } from "contexts/scribe/scribe-provider";
import { current } from "@reduxjs/toolkit";
import CustomLottie from "../../components/custom-lottie";
import { validateName } from "utils/validation";
import useNavigation from "hooks/useNavigation";
import useCustomBadWordsFilter from "../../hooks/useCustomBadWordsFilter";
import accessoriesImage from "accessories.png"; // Import the image
import {animated} from "react-spring";
import useTextFadeSwitcher from "../../hooks/useTextFadeSwitcher";
import AnimatedAccessories from "../../components/accessories/AnimatedAccessories";

// Altezza desiderata per la carta e padding minimo
const desiredCardHeight = 690;
const minimumPadding = 20;

const Result = () => {
  // Estrai il nome dalla route
  const { name: fullName } = useParams<{ name: string }>();
  const lowerCaseFullName = fullName?.toLowerCase();
  const customFilter = useCustomBadWordsFilter();

  // Carica i dati per l'animazione Lottie e i cartigli
  const lottieData = useLottieLoader(lowerCaseFullName);
  const cartigli = useCartigliLoader(lowerCaseFullName);

  // Estrai funzioni di layout dal context
  const {
    setFirstLayerHidden,
    setSecondLayerHidden,
    secondLayerHidden,
    setShouldInfoFadeOut,
    setShowQrAnimation,
    setScribeHidden,
    cardVisibilityFade,
    setCardVisibilityFade, setCardReady, cardReady,
  } = useLayoutContext();

  const {
    setShowQrCode,
    initialized,
    setSwitchToQrCodeScribe,
    setShowWritingAnimation,
    setFullName,
  } = useScribeContext();

  // Riferimento al canvas per il download dell'immagine
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Ottieni altezza del body e triggera l'effetto di visualizzazione
  const { bodyHeight } = useLayoutUtils();
  useShowLayout();

  // Verifica il tipo di dispositivo (PC, mobile, tablet)
  const isPC = useIsPc();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const scalingClass = isTablet
    ? "tablet-scale-04"
    : isMobile
    ? "mobile-scale-03"
    : "scale-05";

  const { navigateToScreensaver } = useNavigation();

  const { switchToQrCodeScribe, showQrCode } = useScribeContext();
  const [isReadyForAnimation, setIsReadyForAnimation] = React.useState<boolean>(false); // Stato per controllare l'animazione

  useEffect(() => {
    // Delay animation readiness to ensure transitions are applied
    const timerId = setTimeout(() => {
      setIsReadyForAnimation(true);
    }, 50); // Ritardo breve per attivare la transizione
    return () => clearTimeout(timerId);
  }, []);

  // Riferimento al contenitore del risultato per gestire lo scaling
  const resultContainerRef = useRef<HTMLDivElement>(null);

  // Hook per il download dell'immagine con geroglifici
  const { downloadImage } = useHieroglyphDownloader({
    userName: lowerCaseFullName,
    cartigli,
    lottieData,
    canvasRef: canvasRef,
  });


  useEffect(() => {

    if (!fullName || !validateName(fullName)) {
      navigateToScreensaver();
      return;
    }

    const filteredFullName = customFilter.filterBadwords(fullName);

    if (filteredFullName !== fullName) {
      // La parola vietata è stata trovata nel fullName, naviga verso lo screensaver
      navigateToScreensaver();
      return;
    }

    setFullName(fullName);
  }, [customFilter, fullName, navigateToScreensaver, setFullName]);

  // Mostra il primo layer e nascondi il secondo layer quando il componente è montato
  useEffect(() => {
    setFirstLayerHidden(false); // visibile
    setSecondLayerHidden(true);
    setShouldInfoFadeOut(false);

    setScribeHidden(false);
    setCardVisibilityFade(true);
    //setSwitchToQrCodeScribe(true);

  }, [
    setScribeHidden,
    setFirstLayerHidden,
    setSecondLayerHidden,
    setCardVisibilityFade,
    setShouldInfoFadeOut,
  ]);

  useEffect(() => {
    if (initialized) return;
    setSwitchToQrCodeScribe(true);
    setShowQrCode(true);  //aggiunto dopo, non fixa il problema
    setShowWritingAnimation(false);
    setShouldInfoFadeOut(false);
  }, [initialized, setShouldInfoFadeOut, setShowQrCode, setShowWritingAnimation, setSwitchToQrCodeScribe]); //rimuovere anche da qui in caso

  //scale card
  useEffect(() => {
    if (bodyHeight === -1) return;
    if (isMobile) return;

    if(cardReady)
    {
      if (bodyHeight < desiredCardHeight) {
        const scale = (bodyHeight - minimumPadding) / desiredCardHeight;

        resultContainerRef.current!.style.transform = `scale(${scale})`;
      }

    }
  }, [bodyHeight, isMobile, cardReady]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setCardReady(true);
    }, 1500);

    return () => {
      clearTimeout(timerId); // Pulisce il timer se il componente viene smontato prima che scada
    };
  }, [setCardReady]);

  if (!cartigli || !lottieData || !fullName) return null;

  return (
    <>
      {
        <>
          <InfoPageGeneratedPage></InfoPageGeneratedPage>
        </>
      }
      {/* Contenitore risultato */}
      {
          (<>
            <div
                ref={resultContainerRef}
                className={`relative flex justify-center align-center direction-column mobile-origin-bottom tablet-origin-bottom ${
                    cardVisibilityFade && cardReady ? "fade-in" : "fade-out"
                }`}
                style={!isPC ? { height: bodyHeight, width: "100%" } : {}}
            >
              {isPC  && <img src={CardImage} alt="Card" />}

              {/* Nome in maiuscolo */}
              <div className={`absolute top-50 mobile-top-20 tablet-top-100 width-100 flex justify-center align-center direction-column gap-10 ${cardReady ? "fade-in" : "fade-out"}`}>
                {fullName
                    ?.toUpperCase()
                    .split(" ")
                    .map((word, wordIndex) => (
                        <div
                            key={`name-${word}-${wordIndex}`}
                            className="font-size-30 fade-in delay-1"
                        >
                          {word}
                        </div>
                    ))}
              </div>

              {/* Animazioni Lottie e geroglifici */}
              {bodyHeight !== -1 && (
                  <div
                      style={isPC ? { height: "97%" } : {}}
                      /*                  className="pc-absolute top-0 width-100 flex justify-center align-end direction-row pc-origin-bottom scale-05 tablet-scale-04 mobile-scale-03 gap-50 "
                       */
                      className={`pc-absolute top-0 width-100 flex justify-center align-end direction-row pc-origin-${
                          lowerCaseFullName?.split(" ").some((word) => word.length > 10)
                              ? "bottom"
                              : "center"
                      } ${scalingClass} gap-50  `}
                  >
                    {lowerCaseFullName?.split(" ").map((word, wordIndex) => (
                        <div
                            key={`animation-${word}-${wordIndex}`}
                            className={`relative `}
                        >
                          <div style={{ width: "120px" }}>
                            <CustomLottie
                                animationData={
                                  cartigli.find((x) => x.length === word.length)
                                      ?.data as object
                                }
                                loop={false}
                                autoplay={true}
                                speed={0.5}
                                height={
                                  (
                                      cartigli.find((x) => x.length === word.length)
                                          ?.data as { h: number }
                                  )?.["h"]
                                }
                            />
                          </div>
                          <div className="absolute top-0 flex justify-center align-center direction-column height-100 width-100 scale-08">
                            {word
                                ?.split("")
                                .map((letter, index) => (
                                    <React.Fragment key={`${index}-${letter}-${wordIndex}`}>
                                      {allowedCharacters.includes(letter) && (
                                          <CustomLottie
                                              animationData={
                                                lottieData.find((l) => l.letter === letter)?.data
                                              }
                                              loop={false}
                                              autoplay={true}
                                              speed={0.5}
                                          />
                                      )}{" "}
                                    </React.Fragment>
                                ))}
                          </div>
                        </div>
                    ))}
                  </div>
              )}



              {/* Bottone di download solo per dispositivi mobile e tablet */}
              {(isMobile || isTablet)  && (
                 <>
                   <div className={`absolute mobile-bottom-10 bottom-50 tablet-bottom-100  opacity-0-not-imp ${cardReady ? "fade-in" : "fade-out"} `}>


                    <div className="flex direction-row align-center " style={{gap: "20px" }}>
                      <button id="download-button"   onClick={downloadImage}>
                        <img src={downloadIcon} alt="download" style={{scale: "150%", padding:"5px"}}/>
                      </button>


                    </div>
                   </div>

                 </>
              )}

              {/* Canvas nascosto per il download dell'immagine */}
              <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
            </div>
          </>)

      }
    </>
  );
};

export default Result;
